// src/store/idb.ts
// A SMALL PROMISE WRAPPER OVER IndexedDB. No dependency, ~120 lines, and it is the only place in
// this app that touches the raw IDB API.
//
// WHY IndexedDB AND NOT localStorage, for all four of the things stored here:
//
//   1. THE BEARER TOKEN. A service worker has no `localStorage` — the API simply does not exist in
//      that global scope. The brief requires the token to be reachable from the service worker, so
//      localStorage is disqualified outright, not merely discouraged.
//   2. THE OFFLINE QUEUE. It holds `File` / `Blob` handles. localStorage stores strings; a Blob put
//      there would have to be base64'd into a 5 MB quota, which a single net-session video blows
//      through immediately. IDB stores Blobs natively and its quota is measured in hundreds of MB.
//   3. THE DEVICE IDENTITY (`client_device_id`). Must survive a force-quit, and must be read by the
//      same code paths as the queue so the two can never disagree about which device this is.
//   4. UPLOAD PROGRESS. Per-part ETags that must outlive a reload.
//
// STORES ARE VERSIONED IN ONE PLACE (`SCHEMA`), because a half-created store is the classic way an
// IDB app works on the developer's machine and fails on a phone that has an older database.

export const DB_NAME = 'dcs-sports-pwa';
export const DB_VERSION = 1;

export const STORE = {
  /** Singleton rows: the bearer session, the device identity. Keyed by a literal string. */
  kv: 'kv',
  /** The offline queue. Keyed by the job's `id`. */
  jobs: 'jobs',
  /** Per-upload durable progress: acknowledged part numbers and their ETags. Keyed by client_upload_uuid. */
  uploads: 'uploads',
} as const;

const SCHEMA: ReadonlyArray<{ name: string; keyPath: string | null }> = [
  { name: STORE.kv, keyPath: null },
  { name: STORE.jobs, keyPath: 'id' },
  { name: STORE.uploads, keyPath: 'client_upload_uuid' },
];

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Test seam: drop the cached handle so a suite can reopen the database from cold.
 *
 * IT MUST `close()` THE LIVE CONNECTION, not merely forget it. An open connection BLOCKS
 * `deleteDatabase` — the delete request fires `onblocked` and then sits pending forever, and the
 * next `open()` queues behind that pending delete and never resolves. Dropping the reference alone
 * leaves the connection open and unreachable, which is the worst of both.
 *
 * The same is true of a real browser: this is what a tab does on unload, so closing here models the
 * reload it is standing in for rather than working around the test runner.
 */
export function __resetDbHandle(): void {
  const pending = dbPromise;
  dbPromise = null;
  if (pending) void pending.then((db) => db.close()).catch(() => undefined);
}

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of SCHEMA) {
        if (db.objectStoreNames.contains(s.name)) continue;
        db.createObjectStore(s.name, s.keyPath ? { keyPath: s.keyPath } : undefined);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB.open failed'));
    req.onblocked = () => reject(new Error('indexedDB.open blocked by another tab'));
  });
  return dbPromise;
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        // Resolve on the REQUEST, reject on the TRANSACTION: a request can succeed and the
        // transaction still abort (quota), and treating that as success is how a queue silently
        // loses a job.
        let value: T;
        req.onsuccess = () => { value = req.result; };
        req.onerror = () => reject(req.error ?? new Error(`${store}: request failed`));
        tx.oncomplete = () => resolve(value);
        tx.onabort = () => reject(tx.error ?? new Error(`${store}: transaction aborted`));
        tx.onerror = () => reject(tx.error ?? new Error(`${store}: transaction failed`));
      }),
  );
}

export function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return run<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
}

/** `key` is omitted for stores with an inline keyPath (jobs, uploads). */
export function idbPut<T>(store: string, value: T, key?: IDBValidKey): Promise<void> {
  return run(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key))).then(() => undefined);
}

export function idbDelete(store: string, key: IDBValidKey): Promise<void> {
  return run(store, 'readwrite', (s) => s.delete(key)).then(() => undefined);
}

export function idbAll<T>(store: string): Promise<T[]> {
  return run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
}

export function idbClear(store: string): Promise<void> {
  return run(store, 'readwrite', (s) => s.clear()).then(() => undefined);
}

/**
 * Read-modify-write inside ONE transaction.
 *
 * The queue's state changes (`queued` → `uploading` → `done`) go through here rather than a
 * get-then-put pair. Two tabs replaying the same queue with separate transactions can interleave
 * between the read and the write and resurrect a job that the other tab just finished — which
 * looks, from the server's side, exactly like a duplicate submission. The idempotency keys make
 * that harmless at the server, but it would still show a wrong count on the screen.
 */
export function idbUpdate<T>(store: string, key: IDBValidKey, fn: (current: T | undefined) => T | null): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        const s = tx.objectStore(store);
        const getReq = s.get(key);
        let out: T | null = null;
        getReq.onsuccess = () => {
          try {
            out = fn(getReq.result as T | undefined);
          } catch (e) {
            reject(e);
            tx.abort();
            return;
          }
          if (out === null) s.delete(key);
          else if (s.keyPath) s.put(out);
          else s.put(out, key);
        };
        getReq.onerror = () => reject(getReq.error ?? new Error(`${store}: read failed`));
        tx.oncomplete = () => resolve(out);
        tx.onabort = () => reject(tx.error ?? new Error(`${store}: transaction aborted`));
      }),
  );
}
